import { getEsvDetails, normalizeCustomerType, updateEsvDetails } from "../../utils/common";

export const GRACE_PERIOD_UPDATED_EVENT = "esv-grace-period-updated";

const GRACE_PERIOD_API_KEYS = [
  "grace_period_status",
  "grace_ends_on",
  "grace_days_left",
  "grace_amount_due",
  "grace_days_overdue",
  "invoice_id",
  "invoice_amount_due",
  "invoice_pdf_path",
];

function pickGracePeriodFields(source = {}, { onlyPresent = false } = {}) {
  return GRACE_PERIOD_API_KEYS.reduce((fields, key) => {
    if (!onlyPresent || key in source) {
      fields[key] = source[key] ?? null;
    }
    return fields;
  }, {});
}

const emptyGracePeriodSnapshot = {
  ...pickGracePeriodFields(),
  customer_type: "normal",
};

export function readGracePeriodFromSession() {
  const stored = getEsvDetails().gracePeriod;
  if (!stored || typeof stored !== "object") {
    return { ...emptyGracePeriodSnapshot };
  }
  return {
    ...emptyGracePeriodSnapshot,
    ...pickGracePeriodFields(stored),
    customer_type: normalizeCustomerType(stored.customer_type),
  };
}

function writeGracePeriodSnapshot(nextSnapshot) {
  updateEsvDetails({ gracePeriod: nextSnapshot });

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(GRACE_PERIOD_UPDATED_EVENT));
  }

  return nextSnapshot;
}

export function persistGracePeriodSnapshot(snapshot = {}) {
  return writeGracePeriodSnapshot({
    ...readGracePeriodFromSession(),
    ...snapshot,
  });
}

export function replaceGracePeriodSnapshot(snapshot = {}) {
  return writeGracePeriodSnapshot({
    ...emptyGracePeriodSnapshot,
    ...snapshot,
  });
}

export function persistGracePeriodFromApiPayload(data) {
  if (!data || typeof data !== "object") {
    return null;
  }

  const result = data.result;
  const nestedResult = result && typeof result === "object" && !Array.isArray(result) ? result : null;
  const snapshot = {
    ...(nestedResult ? pickGracePeriodFields(nestedResult, { onlyPresent: true }) : {}),
    ...pickGracePeriodFields(data, { onlyPresent: true }),
  };

  if (nestedResult && "customer_type" in nestedResult) {
    snapshot.customer_type = normalizeCustomerType(nestedResult.customer_type);
  } else if ("customer_type" in data) {
    snapshot.customer_type = normalizeCustomerType(data.customer_type);
  }

  if (Object.keys(snapshot).length === 0) {
    return null;
  }

  return persistGracePeriodSnapshot(snapshot);
}

export function buildGracePeriodSnapshotFromCompany(companyData) {
  if (!companyData || typeof companyData !== "object") {
    return { ...emptyGracePeriodSnapshot };
  }

  return {
    ...emptyGracePeriodSnapshot,
    ...pickGracePeriodFields(companyData),
    customer_type: normalizeCustomerType(companyData.customer_type),
  };
}

export function isSubscriptionPlan(customerType) {
  return normalizeCustomerType(customerType) === "subscription";
}

function getStatusToken(status) {
  return String(status || "").toUpperCase();
}

export function isGraceActive(status, customerType) {
  if (!isSubscriptionPlan(customerType)) {
    return false;
  }

  const value = getStatusToken(status);
  return value.includes("ACTIVE") && !value.includes("INACTIVE");
}

export function isAccountFrozen(status, customerType) {
  if (!isSubscriptionPlan(customerType)) {
    return false;
  }

  const value = getStatusToken(status);
  return value.includes("OVER_GRACE") || value.includes("FROZEN");
}

export function areResultsLocked(status, customerType) {
  return isGraceActive(status, customerType) || isAccountFrozen(status, customerType);
}

// Spec flags that reveal a result: 1 In Spec, 2 Out of Spec, 3 Spec Incomplete, 5 In Spec (Retest), 6 Out of Spec (Retest)
const RESULT_REVEALING_SPEC_FLAGS = new Set(["1", "2", "3", "5", "6"]);

export function isResultRevealingSpec(specsFlag) {
  return RESULT_REVEALING_SPEC_FLAGS.has(String(specsFlag ?? ""));
}

// Hide In Spec / Out of Spec appearance by falling back to In Process.
export function getVisibleSpecFlag(specsFlag, resultsLocked) {
  if (resultsLocked && isResultRevealingSpec(specsFlag)) {
    return "0";
  }
  return specsFlag;
}

export function isFrozenExemptPath(pathname) {
  // Frozen accounts can still open billing, help, and their own profile
  return ["/myaccount/invoice", "/myaccount/usage-metrics", "/myaccount/help", "/myaccount/profile"].some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

function parseGraceDate(dateValue) {
  if (!dateValue) {
    return null;
  }

  if (typeof dateValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    const [year, month, day] = dateValue.split("-").map(Number);
    const parsedDate = new Date(year, month - 1, day);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const parsedDate = new Date(dateValue);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

export function formatGraceEndDate(dateValue) {
  const parsedDate = parseGraceDate(dateValue);
  if (!parsedDate) {
    return "";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(parsedDate);
}

export function formatDaysRemaining(daysRemaining) {
  const days = Number(daysRemaining);
  if (Number.isNaN(days)) {
    return "";
  }
  return `${days} ${days === 1 ? "day" : "days"} left`;
}

/** e.g. "7-day" / "1-day"; empty when days are unknown. */
export function formatGracePeriodDays(daysRemaining) {
  const days = Number(daysRemaining);
  if (Number.isNaN(days)) {
    return "";
  }
  return `${days}-day`;
}

export function getDaysSince(dateValue) {
  const endedOn = parseGraceDate(dateValue);
  if (!endedOn) {
    return null;
  }

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const startOfEndedOn = new Date(endedOn);
  startOfEndedOn.setHours(0, 0, 0, 0);

  return Math.max(0, Math.round((startOfToday - startOfEndedOn) / 86400000));
}

export function parseDaysOverdue(daysOverdue, fallbackDate) {
  if (daysOverdue != null && daysOverdue !== "") {
    const days = Number(daysOverdue);
    if (!Number.isNaN(days)) {
      return Math.max(0, days);
    }
  }

  return getDaysSince(fallbackDate);
}

export function formatAmountDue(amount) {
  if (amount == null || amount === "") {
    return "";
  }

  const value = Number(amount);
  if (Number.isNaN(value)) {
    return "";
  }

  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function stripResultRevealingFilters(selectedValues = []) {
  return selectedValues.filter((value) => {
    const item = String(value);
    // Drop In Spec / Out of Spec filters: "1" | "2" | "in_spec" | "out_of_spec"
    return item !== "1" && item !== "2" && item !== "in_spec" && item !== "out_of_spec";
  });
}
